import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Section from '../components/Section';
import {BottomNavigator} from '../components/Navbar';
import usePodcasts from '../hooks/usePodcasts';

const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '0:00';
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const formatDate = (timestamp, locale) => {
    if (!timestamp) {
        return '';
    }

    return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    }).format(timestamp);
};

const Icon = ({children, className = ''}) => (
    <span aria-hidden="true" className={`PodcastIcon ${className}`}>{children}</span>
);

const PodcastControls = memo(({audioRequestUrl, loadAsset, title}) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [audioError, setAudioError] = useState(false);
    const [playbackAttempted, setPlaybackAttempted] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);

    useEffect(() => () => {
        audioRef.current?.pause();
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
        }
    }, [audioUrl]);

    useEffect(() => {
        if (audioUrl && playbackAttempted) {
            audioRef.current?.play().catch(() => {
                setIsLoadingAudio(false);
                setAudioError(true);
            });
        }
    }, [audioUrl, playbackAttempted]);

    const togglePlayback = useCallback(async () => {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }

        if (audio.paused) {
            try {
                setPlaybackAttempted(true);
                if (!audioUrl) {
                    setAudioError(false);
                    setIsLoadingAudio(true);
                    setAudioUrl(await loadAsset(audioRequestUrl));
                    return;
                }
                await audio.play();
            } catch (error) {
                setIsLoadingAudio(false);
                setAudioError(true);
            }
        } else {
            audio.pause();
        }
    }, [audioRequestUrl, audioUrl, loadAsset]);

    const seek = useCallback((amount) => {
        const audio = audioRef.current;
        if (audio) {
            audio.currentTime = Math.max(0, Math.min(audio.duration || 0, audio.currentTime + amount));
        }
    }, []);

    const updateProgress = useCallback(() => {
        const audio = audioRef.current;
        if (audio) {
            setCurrentTime(audio.currentTime);
        }
    }, []);

    const updateDuration = useCallback(() => {
        const audio = audioRef.current;
        if (audio && Number.isFinite(audio.duration)) {
            setDuration(audio.duration);
        }
    }, []);

    const handleSliderChange = useCallback((event) => {
        const audio = audioRef.current;
        if (audio) {
            audio.currentTime = Number(event.target.value);
        }
    }, []);

    return (
        <div className="PodcastControls">
            <audio
                ref={audioRef}
                preload="metadata"
                src={audioUrl || undefined}
                onPlay={() => {
                    setIsLoadingAudio(false);
                    setIsPlaying(true);
                }}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onTimeUpdate={updateProgress}
                onLoadedMetadata={updateDuration}
                onError={() => {
                    if (playbackAttempted) {
                        setIsLoadingAudio(false);
                        setAudioError(true);
                    }
                }}
                aria-label={`${title} audio player`}
            />
            <input
                aria-label={`Playback progress for ${title}`}
                className="PodcastControls__progress"
                type="range"
                min="0"
                max={duration || 1}
                value={Math.min(currentTime, duration || 1)}
                onChange={handleSliderChange}
                style={{'--progress': `${duration ? (currentTime / duration) * 100 : 0}%`}}
            />
            <div className="PodcastControls__timestamps">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
            </div>
            <div className="PodcastControls__actions">
                <button type="button" className="PodcastControls__seek" onClick={() => seek(-15)} aria-label="Back 15 seconds">
                    <span aria-hidden="true">≪</span>
                </button>
                <button
                    type="button"
                    className="PodcastControls__play"
                    onClick={togglePlayback}
                    aria-label={isLoadingAudio ? `Loading ${title}` : (isPlaying ? `Pause ${title}` : `Play ${title}`)}
                    aria-busy={isLoadingAudio}
                    disabled={isLoadingAudio}
                >
                    {isLoadingAudio ? <span className="PodcastControls__spinner" aria-hidden="true" /> : <Icon>{isPlaying ? 'Ⅱ' : '▶'}</Icon>}
                </button>
                <button type="button" className="PodcastControls__seek" onClick={() => seek(15)} aria-label="Forward 15 seconds">
                    <span aria-hidden="true">≫</span>
                </button>
            </div>
            {audioError && <p className="PodcastControls__error">Audio is temporarily unavailable.</p>}
        </div>
    );
});

const PodcastArtwork = memo(({requestUrl, title, loadAsset}) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [hasArtworkError, setHasArtworkError] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        let objectUrl;
        loadAsset(requestUrl, controller.signal)
            .then((url) => {
                objectUrl = url;
                setImageUrl(url);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    setHasArtworkError(true);
                }
            });

        return () => {
            controller.abort();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [loadAsset, requestUrl]);

    return (
        <div className="PodcastItem__artwork">
            {imageUrl && !hasArtworkError ? <img src={imageUrl} alt={`Cover art for ${title}`} /> : <span aria-hidden="true">L</span>}
        </div>
    );
});

const PodcastItem = memo(({podcast, locale, loadAsset}) => {
    return (
        <article className="PodcastItem">
            <PodcastArtwork requestUrl={podcast.artworkRequestUrl} title={podcast.title} loadAsset={loadAsset} />
            <div className="PodcastItem__content">
                <p className="PodcastItem__meta">{formatDate(podcast.createdAt, locale)} · {podcast.topic}</p>
                <h2>{podcast.title}</h2>
            </div>
            <PodcastControls audioRequestUrl={podcast.audioRequestUrl} loadAsset={loadAsset} title={podcast.title} />
        </article>
    );
});

const PodcastPage = () => {
    const {t, i18n} = useTranslation();
    const {podcasts, loading, error, refresh, fetchPodcastAsset} = usePodcasts();

    return (
        <div className="Page PodcastPage">
            <Section className="PodcastSection" id="podcast">
                <div className="PodcastSection__heading">
                    <div>
                        <h1>{t('podcast.title')}</h1>
                        <p>{t('podcast.subtitle')}</p>
                    </div>
                    <button type="button" className="PodcastSection__refresh" onClick={refresh} disabled={loading}>
                        {loading ? t('podcast.loading') : t('podcast.refresh')}
                    </button>
                </div>

                {loading && <p className="PodcastSection__status">{t('podcast.loading')}</p>}
                {error && (
                    <div className="PodcastSection__status PodcastSection__status--error">
                        <p>{error}</p>
                        <button type="button" onClick={refresh}>{t('podcast.retry')}</button>
                    </div>
                )}
                {!loading && !error && podcasts.length === 0 && (
                    <p className="PodcastSection__status">{t('podcast.empty')}</p>
                )}
                {podcasts.length > 0 && (
                    <div className="PodcastItems">
                        {podcasts.map((podcast) => (
                            <PodcastItem key={podcast.id} podcast={podcast} locale={i18n.language} loadAsset={fetchPodcastAsset} />
                        ))}
                    </div>
                )}
            </Section>
            <BottomNavigator next={{to: '/news', title: t('navbar.news')}} />
        </div>
    );
};

export default PodcastPage;
